/**
 * Bangladesh VAT calculations.
 * Add VAT:    VAT = Net × Rate/100;        Gross = Net + VAT
 * Remove VAT: Net = Gross / (1 + Rate/100); VAT  = Gross − Net
 */

export type VatDirection = "add" | "remove";

export interface VatResult {
  net: number;
  vat: number;
  gross: number;
  ratePct: number;
}

export function computeVat(
  direction: VatDirection,
  amount: number,
  ratePct: number,
): VatResult {
  const safeAmt = Math.max(0, Number.isFinite(amount) ? amount : 0);
  const safeRate = Math.max(0, Number.isFinite(ratePct) ? ratePct : 0);
  const f = safeRate / 100;

  if (direction === "add") {
    const net = safeAmt;
    const vat = net * f;
    return { net, vat, gross: net + vat, ratePct: safeRate };
  }
  const gross = safeAmt;
  const net = f === 0 ? gross : gross / (1 + f);
  const vat = gross - net;
  return { net, vat, gross, ratePct: safeRate };
}

export interface LineItem {
  id: string;
  label: string;
  amount: number;
  ratePct: number;
  direction: VatDirection;
}

export interface InvoiceTotals {
  totalNet: number;
  totalVat: number;
  totalGross: number;
  lines: (LineItem & VatResult)[];
}

export function computeInvoice(items: LineItem[]): InvoiceTotals {
  const lines = items.map((it) => {
    const r = computeVat(it.direction, it.amount, it.ratePct);
    return { ...it, ...r };
  });
  const totalNet = lines.reduce((s, l) => s + l.net, 0);
  const totalVat = lines.reduce((s, l) => s + l.vat, 0);
  const totalGross = lines.reduce((s, l) => s + l.gross, 0);
  return { totalNet, totalVat, totalGross, lines };
}
